import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, Minus, Plus, ArrowLeft } from "lucide-react";

export default function Produit() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    setLoading(true);
    setActiveImage(0);
    api.get(`/shop/products/${productId}`).then(({ data }) => {
      setProduct(data);
      setSize(data.sizes?.length ? data.sizes[0] : null);
      setQuantity(1);
      if (data.category) {
        api.get("/shop/products").then(({ data: all }) => {
          setRelated(all.filter((p) => p.category === data.category && p.product_id !== data.product_id).slice(0, 4));
        }).catch(() => {});
      } else {
        setRelated([]);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [productId]);

  const handleAdd = () => {
    if (product.sizes?.length && !size) {
      toast.error("Choisis une taille");
      return;
    }
    addToCart(product, size, quantity);
    toast.success("Ajouté au panier");
    navigate("/panier");
  };

  if (loading) return <div className="max-w-5xl mx-auto px-5 pt-10 pb-16 text-center text-white/40">Chargement...</div>;
  if (!product) {
    return (
      <div className="max-w-5xl mx-auto px-5 pt-10 pb-16 text-center">
        <p className="text-white/50 mb-4">Produit introuvable.</p>
        <Link to="/" className="text-[#00F0FF] hover:underline">Retour à la boutique</Link>
      </div>
    );
  }

  const outOfStock = product.stock === 0;
  const lowStock = product.stock !== null && product.stock !== undefined && product.stock > 0 && product.stock <= 3;

  return (
    <div className="max-w-5xl mx-auto px-5 pt-10 pb-16">
      <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition mb-6">
        <ArrowLeft size={14} /> Retour à la boutique
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <div className="card-glass aspect-square overflow-hidden bg-white/5 mb-3">
            {product.images?.length ? (
              <img src={product.images[activeImage]} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><ShoppingBag size={40} className="text-white/20" /></div>
            )}
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-2">
              {product.images.map((url, i) => (
                <button key={url} onClick={() => setActiveImage(i)} className={`w-16 h-16 rounded-lg overflow-hidden border ${i === activeImage ? "border-[#00F0FF]" : "border-white/10"}`}>
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {product.category && <span className="text-[10px] uppercase tracking-widest text-white/40">{product.category}</span>}
          <h1 className="font-display font-black text-2xl md:text-3xl tracking-tight mt-1">{product.name}</h1>
          <p className="text-[#00F0FF] font-display font-bold text-xl mt-2">{(product.price_cents / 100).toFixed(2)}€</p>
          {product.description && <p className="text-white/60 text-sm leading-relaxed mt-4 whitespace-pre-line">{product.description}</p>}
          {lowStock && <p className="text-xs text-[#FFD700] mt-3">Plus que {product.stock} en stock</p>}

          {product.sizes?.length > 0 && (
            <div className="mt-6">
              <label className="text-[10px] uppercase tracking-widest text-white/40 font-display mb-2 block">Taille</label>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s} onClick={() => setSize(s)}
                    className={`px-4 py-2 rounded-full text-xs font-bold border transition-all ${size === s ? "bg-[#00F0FF] text-black border-[#00F0FF]" : "border-white/10 text-white/70 hover:border-white/30"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6">
            <label className="text-[10px] uppercase tracking-widest text-white/40 font-display mb-2 block">Quantité</label>
            <div className="flex items-center gap-3">
              <button onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10"><Minus size={14} /></button>
              <span className="font-display font-bold w-6 text-center">{quantity}</span>
              <button onClick={() => setQuantity((q) => Math.min(20, q + 1))} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10"><Plus size={14} /></button>
            </div>
          </div>

          <button onClick={handleAdd} disabled={outOfStock} className="btn-neon w-full !py-3 mt-8 disabled:opacity-40 disabled:cursor-not-allowed">
            {outOfStock ? "Épuisé" : "Ajouter au panier"}
          </button>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-16">
          <h2 className="font-display font-bold text-xl mb-5">Dans la même catégorie</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {related.map((p) => (
              <Link key={p.product_id} to={`/produit/${p.product_id}`} className="card-glass overflow-hidden group hover:border-white/20 transition-all">
                <div className="aspect-square overflow-hidden bg-white/5">
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><ShoppingBag size={24} className="text-white/20" /></div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="font-display font-bold text-xs leading-tight truncate">{p.name}</h3>
                  <p className="text-[#00F0FF] font-display font-bold text-sm mt-1">{(p.price_cents / 100).toFixed(2)}€</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
